import { useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  Button,
  TextField,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Collapse,
  IconButton,
  Divider,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import useAdminCockpit from '../../hooks/useAdminCockpit'
import { RequestService, STATUSES } from '../../services/RequestService'
import { useSnackbar } from '../../hooks'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f1f3f5;
  padding: 32px 48px;

  h2 {
    margin-top: 20px;
  }
`

const TopBoxes = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 32px;
`

const BoxItem = styled.div`
  flex: 1;
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  font-weight: bold;

  p {
    margin-top: 8px;
    font-size: 22px;
  }
`

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin: 24px 0 0 0;
  gap: 8px;

  & > .MuiTextField-root {
    margin-bottom: 16px;
  }
`

const InlineField = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`

const emptyAccountForm = () => ({
  id: '',
  name: '',
  company_name: '',
  cnpj: '',
})

const emptySubscriptionForm = () => ({
  plan_id: '',
  start_date: '',
  end_date: '',
  payment_granularity: '',
  payment_method: '',
  status: '',
  user_count: '',
  base_user_value: '',
  add_on_value: '',
  trial_ends_at: '',
  discount_code: '',
  discount_value: '',
})

const applyConfiguration = (configuration = {}) => ({
  id: '',
  alertness_benchmark: configuration.alertness_benchmark ?? '',
  caffeine_limit: configuration.caffeine_limit ?? '',
  attention_threshold: configuration.attention_threshold ?? '',
  impairment_threshold: configuration.impairment_threshold ?? '',
  bac_8: configuration.bac_8 ?? '',
  bac_6: configuration.bac_6 ?? '',
  pvt_time: configuration.pvt_time ?? '',
  pvt_message: configuration.pvt_message ?? '',
  intake_message: configuration.intake_message ?? '',
  config_message: configuration.config_message ?? '',
  schedule_message: configuration.schedule_message ?? '',
  pvt_count: configuration.pvt_count ?? '',
  pvt_wait: configuration.pvt_wait ?? '',
  push_notifications: !!configuration.push_notifications,
  pvt_active: !!configuration.pvt_active,
  bac_8_active: !!configuration.bac_8_active,
  bac_6_active: !!configuration.bac_6_active,
  collaborator_can_navigate: !!configuration.collaborator_can_navigate,
})

const ConfigurationFields = ({ data, onChange }) => {
  const textFields = [
    [
      'Mensagem sem PVT',
      'pvt_message',
      'text',
      'Mensagem padrão em notificação de PVT faltando',
    ],
    [
      'Mensagem sem Registro Diário',
      'intake_message',
      'text',
      'Mensagem padrão em notificação de Registro Diário faltando',
    ],
    [
      'Mensagem sem Onboarding',
      'config_message',
      'text',
      'Mensagem padrão de Registro Inicial faltando',
    ],
    [
      'Mensagem sem Turno',
      'schedule_message',
      'text',
      'Mensagem padrão de Turno faltando',
    ],
    ['Duração PVT', 'pvt_time', 'number', 'Duração do PVT em min'],
    ['Quantidade de PVT', 'pvt_count', 'number', 'Quantidade de PVTs da conta'],
    ['Espera entre PVT', 'pvt_wait', 'number', 'Tempo de espera entre PVTs'],
    [
      'BAC 8',
      'bac_8',
      'number',
      'Posição da linha do BAC 0,08 no gráfico do usuário',
    ],
    [
      'BAC 6',
      'bac_6',
      'number',
      'Posição da linha do BAC 0,06 no gráfico do usuário',
    ],
    [
      'Limiar de Alerta',
      'attention_threshold',
      'number',
      'Alvo da otimização da Engine - Equivalente a 80% no gráfico',
    ],
    [
      'Limiar Alerta Crítico',
      'impairment_threshold',
      'number',
      'Equivalente a 60% no gráfico',
    ],
    [
      'Limite de cafeína',
      'caffeine_limit',
      'number',
      'Limite diário de consumo de cafeína sugerida pela Engine',
    ],
    [
      'Benchmark de alerta',
      'alertness_benchmark',
      'number',
      'Benchmark de alerta para relatório Distribuição de zona de fadiga',
    ],
  ]
  const switches = [
    [
      'Notificações Push',
      'push_notifications',
      'Ativado o envio de notificações Push',
    ],
    ['PVT Ativo', 'pvt_active', 'Ativar e desativar PVT na conta'],
    [
      'Colaborador pode navegar',
      'collaborator_can_navigate',
      'Ativar para colaborador ser redirecionado em 10s após Registro Diário',
    ],
    [
      'BAC 8 Ativo',
      'bac_8_active',
      'Ativar a linha do BAC 0,08 no gráfico do usuário',
    ],
    [
      'BAC 6 Ativo',
      'bac_6_active',
      'Ativar a linha do BAC 0,06 no gráfico do usuário',
    ],
  ]

  return (
    <>
      {textFields.map(([label, name, type, tooltip]) => (
        <Tooltip
          key={name}
          title={tooltip}
        >
          <TextField
            label={label}
            type={type}
            fullWidth
            name={name}
            value={data[name]}
            onChange={onChange}
          />
        </Tooltip>
      ))}
      {switches.map(([label, name, tooltip]) => (
        <Tooltip
          key={name}
          title={tooltip}
        >
          <InlineField>
            <FormControlLabel
              control={
                <Switch
                  checked={data[name]}
                  onChange={onChange}
                  name={name}
                />
              }
              label={label}
            />
          </InlineField>
        </Tooltip>
      ))}
    </>
  )
}

const AdminCockpit = () => {
  const {
    loadingData: loading,
    data: adminCockpitData,
    plans,
  } = useAdminCockpit()
  const { successSnackbar, errorSnackbar } = useSnackbar()

  const [accountForm, setAccountForm] = useState({
    current_account_id: '',
  })
  const [showConfig, setShowConfig] = useState(true)
  const [showSharedConfig, setShowSharedConfig] = useState(true)
  const [showAccountSection, setShowAccountSection] = useState(true)
  const [showCreateAccountSection, setShowCreateAccountSection] = useState(true)

  const [selectedAccountId, setSelectedAccountId] = useState('')
  const [accountDataForm, setAccountDataForm] = useState(emptyAccountForm)
  const [subscriptionForm, setSubscriptionForm] = useState(
    emptySubscriptionForm,
  )
  const [newAccountDataForm, setNewAccountDataForm] = useState(emptyAccountForm)
  const [newSubscriptionForm, setNewSubscriptionForm] = useState(
    emptySubscriptionForm,
  )

  const handleAccountSubmit = async event => {
    event.preventDefault()
    try {
      const response = await RequestService.updateCurrentAccount(accountForm)

      if (response.status === 'success') {
        successSnackbar({ text: 'Conta atualizada com sucesso' })
        setSelectedAccountId(accountForm.current_account_id)
        await loadAccountForms(accountForm.current_account_id)
      } else {
        errorSnackbar({ text: 'Falha ao atualizar conta' })
      }
    } catch (e) {
      errorSnackbar({ text: 'Falha ao atualizar conta' })
    }
  }

  const [formData, setFormData] = useState({
    id: '',
    alertness_benchmark: '',
    caffeine_limit: '',
    attention_threshold: '',
    impairment_threshold: '',
    bac_8: '',
    bac_6: '',
    pvt_time: '',
    pvt_message: '',
    intake_message: '',
    config_message: '',
    schedule_message: '',
    pvt_count: '',
    pvt_wait: '',
    push_notifications: false,
    pvt_active: false,
    bac_8_active: false,
    bac_6_active: false,
    collaborator_can_navigate: false,
  })
  const [sharedFormData, setSharedFormData] = useState(() =>
    applyConfiguration(),
  )

  useEffect(() => {
    if (adminCockpitData?.config) {
      setSharedFormData(applyConfiguration(adminCockpitData.config))
    }
  }, [adminCockpitData?.config])

  useEffect(() => {
    const accountId = adminCockpitData?.currentAccount?.id
    if (!accountId) return

    setAccountForm({ current_account_id: accountId })
    setSelectedAccountId(accountId)
    loadAccountForms(accountId)
  }, [adminCockpitData?.currentAccount?.id])

  const loadAccountForms = async accountId => {
    try {
      const [configurationResponse, accountResponse] = await Promise.all([
        RequestService.getAccountConfiguration(accountId),
        RequestService.getAccountSubscriptionData(accountId),
      ])

      if (configurationResponse.status === STATUSES.SUCCESS) {
        setFormData(applyConfiguration(configurationResponse.data.config))
      }
      if (accountResponse.status === STATUSES.SUCCESS) {
        setAccountDataForm({
          ...emptyAccountForm(),
          ...accountResponse.data?.account,
        })
        setSubscriptionForm({
          ...emptySubscriptionForm(),
          ...accountResponse.data?.subscription,
        })
      }
    } catch (e) {
      errorSnackbar({ text: 'Erro ao carregar dados da conta' })
    }
  }

  const handleAccountChange = event => {
    setAccountForm({ current_account_id: event.target.value })
  }

  const handleInputChange = async event => {
    const { name, value, type, checked } = event.target
    const newValue = type === 'checkbox' ? checked : value

    setFormData(prevData => ({ ...prevData, [name]: newValue }))
  }

  const handleSharedInputChange = event => {
    const { name, value, type, checked } = event.target
    setSharedFormData(previous => ({
      ...previous,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleSubmit = async (
    event,
    selectedAccount,
    configuration = formData,
  ) => {
    event.preventDefault()
    try {
      if (selectedAccount === 'all_accounts') {
        const response = await RequestService.updateAllAccounts({
          formData: configuration,
        })
      } else if (selectedAccount === 'current') {
        const response = await RequestService.updateAccountConfigurations({
          formData,
        })
      } else {
        const response = await RequestService.updateSystemConfiguration({
          formData: configuration,
        })
      }
      successSnackbar({ text: 'Configuração atualizada' })
    } catch (e) {
      errorSnackbar({ text: 'Falha ao atualizar' })
    }
  }

  const handleAccountDataFormChange = event => {
    const { name, value } = event.target
    setAccountDataForm(prev => ({ ...prev, [name]: value }))
  }

  const handleSubscriptionFormChange = event => {
    const { name, value } = event.target
    setSubscriptionForm(prev => ({ ...prev, [name]: value }))
  }

  const handleNewAccountDataFormChange = event => {
    const { name, value } = event.target
    setNewAccountDataForm(prev => ({ ...prev, [name]: value }))
  }

  const handleNewSubscriptionFormChange = event => {
    const { name, value } = event.target
    setNewSubscriptionForm(prev => ({ ...prev, [name]: value }))
  }

  const validateForms = (account, subscription) => {
    if (!account.name.trim()) return 'Nome da conta é obrigatório'
    if (!subscription.plan_id) return 'Plano é obrigatório'
    if (!subscription.start_date) return 'Data de início é obrigatória'
    if (!subscription.end_date) return 'Data de término é obrigatória'
    if (!subscription.payment_granularity.trim())
      return 'Granularidade de pagamento é obrigatória'
    if (!subscription.payment_method.trim())
      return 'Forma de pagamento é obrigatória'
    if (!subscription.status.trim()) return 'Status é obrigatório'

    const start = new Date(subscription.start_date)
    const end = new Date(subscription.end_date)
    if (end < start) {
      return 'Data de término deve ser após a data de início'
    }

    return null
  }

  const renderAccountSubscriptionFields = (
    account,
    subscription,
    onAccountChange,
    onSubscriptionChange,
  ) => (
    <>
      <TextField
        label='Nome da Conta'
        name='name'
        fullWidth
        value={account.name}
        onChange={onAccountChange}
      />
      <TextField
        label='Razão Social'
        name='company_name'
        fullWidth
        value={account.company_name}
        onChange={onAccountChange}
      />
      <TextField
        label='CNPJ'
        name='cnpj'
        fullWidth
        value={account.cnpj}
        onChange={onAccountChange}
      />
      <Divider style={{ margin: '20px 0' }} />
      <FormControl
        fullWidth
        margin='normal'
      >
        <InputLabel>Plano</InputLabel>
        <Select
          name='plan_id'
          value={subscription.plan_id}
          onChange={onSubscriptionChange}
          label='Plano'
        >
          <MenuItem value=''>Selecione um plano</MenuItem>
          {plans
            ?.slice()
            .sort((a, b) => a.id - b.id)
            .map(plan => (
              <MenuItem
                key={plan.id}
                value={plan.id}
              >
                {plan.name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      {[
        ['Data de Início', 'start_date', 'date'],
        ['Data de Término', 'end_date', 'date'],
      ].map(([label, name, type]) => (
        <TextField
          key={name}
          label={label}
          type={type}
          name={name}
          fullWidth
          value={subscription[name]}
          onChange={onSubscriptionChange}
          InputLabelProps={{ shrink: true }}
        />
      ))}
      <FormControl
        fullWidth
        margin='normal'
      >
        <InputLabel>Granularidade de Pagamento</InputLabel>
        <Select
          name='payment_granularity'
          value={subscription.payment_granularity}
          onChange={onSubscriptionChange}
          label='Granularidade de Pagamento'
        >
          <MenuItem value='daily'>Diária</MenuItem>
          <MenuItem value='weekly'>Semanal</MenuItem>
          <MenuItem value='monthly'>Mensal</MenuItem>
          <MenuItem value='yearly'>Anual</MenuItem>
        </Select>
      </FormControl>
      <TextField
        label='Método de Pagamento'
        name='payment_method'
        fullWidth
        value={subscription.payment_method}
        onChange={onSubscriptionChange}
      />
      <FormControl
        fullWidth
        margin='normal'
      >
        <InputLabel>Status da Assinatura</InputLabel>
        <Select
          name='status'
          value={subscription.status}
          onChange={onSubscriptionChange}
          label='Status da Assinatura'
        >
          <MenuItem value='pending'>Pendente</MenuItem>
          <MenuItem value='active'>Ativa</MenuItem>
          <MenuItem value='canceled'>Cancelada</MenuItem>
          <MenuItem value='trialing'>Em teste</MenuItem>
          <MenuItem value='locked'>Bloqueada</MenuItem>
        </Select>
      </FormControl>
      {[
        ['Limite de usuários', 'user_count', 'number'],
        ['Valor base por usuário', 'base_user_value', 'number'],
        ['Valor de Acréscimo', 'add_on_value', 'number'],
        ['Fim do Período de Testes', 'trial_ends_at', 'date'],
        ['Código de Desconto', 'discount_code', 'text'],
        ['Valor de Desconto', 'discount_value', 'number'],
      ].map(([label, name, type]) => (
        <TextField
          key={name}
          label={label}
          type={type}
          name={name}
          fullWidth
          value={subscription[name]}
          onChange={onSubscriptionChange}
          InputLabelProps={type === 'date' ? { shrink: true } : undefined}
        />
      ))}
    </>
  )

  const saveAccountAndSubscription = async (
    event,
    account,
    subscription,
    accountId,
  ) => {
    event.preventDefault()

    const validationError = validateForms(account, subscription)
    if (validationError) {
      errorSnackbar({ text: validationError })
      return
    }

    try {
      const response =
        await RequestService.createOrUpdateAccountAndSubscription({
          account,
          subscription: { ...subscription, account_id: accountId },
        })

      if (response.status === 'success') {
        successSnackbar({
          text: accountId
            ? 'Conta e assinatura atualizadas'
            : 'Conta e assinatura criadas',
        })
        window.location.reload()
      } else {
        errorSnackbar({ text: 'Falha ao salvar' })
      }
    } catch (e) {
      errorSnackbar({ text: 'Erro ao salvar dados' })
    }
  }

  return (
    <Container>
      <TopBoxes>
        <BoxItem>
          <h3>Total de Usuários </h3>
          <p>{`${adminCockpitData?.userCount}` || 'Carregando...'}</p>
        </BoxItem>
        <BoxItem>
          <h3>Total de Contas</h3>
          <p>{adminCockpitData?.accountCount || 'Carregando...'}</p>
        </BoxItem>
      </TopBoxes>

      {/* Account Selection Form */}
      <h2>Seleção de Conta</h2>
      <FormContainer
        as='form'
        onSubmit={handleAccountSubmit}
      >
        <FormControl
          fullWidth
          margin='normal'
        >
          <InputLabel>Conta atual</InputLabel>
          <Select
            name='current_account_id'
            value={accountForm.current_account_id}
            onChange={handleAccountChange}
            label='Conta atual'
          >
            <MenuItem value=''>Selecione uma conta</MenuItem>
            {adminCockpitData?.accountList?.map(account => (
              <MenuItem
                key={account.id}
                value={account.id}
              >
                {account.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant='contained'
          color='primary'
          type='submit'
          disabled={!accountForm.current_account_id}
        >
          Alterar Conta Logada
        </Button>
      </FormContainer>

      <div
        style={{
          display: 'flex',
          gap: '24px',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{ width: '48%', boxSizing: 'border-box', marginBottom: 24 }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <h2>Configurações Gerais</h2>
            <IconButton onClick={() => setShowConfig(!showConfig)}>
              {showConfig ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </div>
          <Collapse in={showConfig}>
            <FormContainer
              as='form'
              onSubmit={event => handleSubmit(event, 'current')}
            >
              <Tooltip title='Mensagem padrão em notificação de PVT faltando'>
                <TextField
                  label='Mensagem sem PVT'
                  fullWidth
                  name='pvt_message'
                  value={formData.pvt_message}
                  onChange={handleInputChange}
                />
              </Tooltip>
              <Tooltip title='Mensagem padrão em notificação de Registro Diário faltando'>
                <TextField
                  label='Mensagem sem Registro Diário'
                  fullWidth
                  name='intake_message'
                  value={formData.intake_message}
                  onChange={handleInputChange}
                />
              </Tooltip>
              <Tooltip title='Mensagem padrão de Registro Inicial faltando'>
                <TextField
                  label='Mensagem sem Onboarding'
                  fullWidth
                  name='config_message'
                  value={formData.config_message}
                  onChange={handleInputChange}
                />
              </Tooltip>
              <Tooltip title='Mensagem padrão de Turno faltando'>
                <TextField
                  label='Mensagem sem Turno'
                  fullWidth
                  name='schedule_message'
                  value={formData.schedule_message}
                  onChange={handleInputChange}
                />
              </Tooltip>
              <Tooltip title='Duração do PVT em min'>
                <TextField
                  label='Duração PVT'
                  type='number'
                  fullWidth
                  name='pvt_time'
                  value={formData.pvt_time}
                  onChange={handleInputChange}
                />
              </Tooltip>
              <Tooltip title='Quantidade de PVTs da conta'>
                <TextField
                  label='Quantidade de PVT'
                  type='number'
                  fullWidth
                  name='pvt_count'
                  value={formData.pvt_count}
                  onChange={handleInputChange}
                />
              </Tooltip>
              <Tooltip title='Tempo de espera entre PVTs'>
                <TextField
                  label='Espera entre PVT'
                  type='number'
                  fullWidth
                  name='pvt_wait'
                  value={formData.pvt_wait}
                  onChange={handleInputChange}
                />
              </Tooltip>
              <Tooltip title='Ativado o envio de notificações Push'>
                <InlineField>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.push_notifications}
                        onChange={handleInputChange}
                        name='push_notifications'
                      />
                    }
                    label='Notificações Push'
                  />
                </InlineField>
              </Tooltip>
              <Tooltip title='Ativar e desativar PVT na conta'>
                <InlineField>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.pvt_active}
                        onChange={handleInputChange}
                        name='pvt_active'
                      />
                    }
                    label='PVT Ativo'
                  />
                </InlineField>
              </Tooltip>
              <Tooltip title='Ativar para colaborador ser redirecionado em 10s após Registro Diário'>
                <InlineField>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.collaborator_can_navigate}
                        onChange={handleInputChange}
                        name='collaborator_can_navigate'
                      />
                    }
                    label='Colaborador pode navegar'
                  />
                </InlineField>
              </Tooltip>
              <Tooltip title='Ativar a linha do BAC 0,08 no gráfico do usuário'>
                <InlineField>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.bac_8_active}
                        onChange={handleInputChange}
                        name='bac_8_active'
                      />
                    }
                    label='BAC 8 Ativo'
                  />
                </InlineField>
              </Tooltip>
              <Tooltip title='Posição da linha do BAC 0,08 no gráfico do usuário'>
                <TextField
                  label='BAC 8'
                  type='number'
                  fullWidth
                  name='bac_8'
                  value={formData.bac_8}
                  onChange={handleInputChange}
                />
              </Tooltip>
              <Tooltip title='Ativar a linha do BAC 0,06 no gráfico do usuário'>
                <InlineField>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.bac_6_active}
                        onChange={handleInputChange}
                        name='bac_6_active'
                      />
                    }
                    label='BAC 6 Ativo'
                  />
                </InlineField>
              </Tooltip>
              <Tooltip title='Posição da linha do BAC 0,06 no gráfico do usuário'>
                <TextField
                  label='BAC 6'
                  type='number'
                  fullWidth
                  name='bac_6'
                  value={formData.bac_6}
                  onChange={handleInputChange}
                />
              </Tooltip>
              <Tooltip title='Alvo da otimização da Engine - Equivalente a 80% no gráfico'>
                <TextField
                  label='Limiar de Alerta'
                  type='number'
                  fullWidth
                  name='attention_threshold'
                  value={formData.attention_threshold}
                  onChange={handleInputChange}
                />
              </Tooltip>
              <Tooltip title='Equivalente a 60% no gráfico'>
                <TextField
                  label='Limiar Alerta Crítico'
                  type='number'
                  fullWidth
                  name='impairment_threshold'
                  value={formData.impairment_threshold}
                  onChange={handleInputChange}
                />
              </Tooltip>
              <Tooltip title='Limite diário de consumo de cafeína sugerida pela Engine'>
                <TextField
                  label='Limite de cafeína'
                  type='number'
                  fullWidth
                  name='caffeine_limit'
                  value={formData.caffeine_limit}
                  onChange={handleInputChange}
                />
              </Tooltip>
              <Tooltip title='Benchmark de alerta para relatório Distribuição de zona de fadiga'>
                <TextField
                  label='Benchmark de alerta'
                  type='number'
                  fullWidth
                  name='alertness_benchmark'
                  value={formData.alertness_benchmark}
                  onChange={handleInputChange}
                />
              </Tooltip>
              <Button
                variant='contained'
                color='primary'
                onClick={e => handleSubmit(e, 'current')}
              >
                Atualizar Conta
              </Button>
            </FormContainer>
          </Collapse>
        </div>

        <div
          style={{ width: '48%', boxSizing: 'border-box', marginBottom: 24 }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <h2>Atualizar Conta e Assinatura</h2>
            <IconButton
              onClick={() => setShowAccountSection(!showAccountSection)}
            >
              {showAccountSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </div>

          <Collapse in={showAccountSection}>
            <FormContainer
              as='form'
              onSubmit={event =>
                saveAccountAndSubscription(
                  event,
                  accountDataForm,
                  subscriptionForm,
                  selectedAccountId,
                )
              }
            >
              <TextField
                label='Nome da Conta'
                name='name'
                fullWidth
                value={accountDataForm.name}
                onChange={handleAccountDataFormChange}
              />
              <TextField
                label='Razão Social'
                name='company_name'
                fullWidth
                value={accountDataForm.company_name}
                onChange={handleAccountDataFormChange}
              />
              <TextField
                label='CNPJ'
                name='cnpj'
                fullWidth
                value={accountDataForm.cnpj}
                onChange={handleAccountDataFormChange}
              />
              <Divider style={{ margin: '20px 0' }} />

              <FormControl
                fullWidth
                margin='normal'
              >
                <InputLabel>Plano</InputLabel>
                <Select
                  name='plan_id'
                  value={subscriptionForm.plan_id}
                  onChange={handleSubscriptionFormChange}
                >
                  <MenuItem value=''>Selecione um plano</MenuItem>

                  {plans
                    ?.slice()
                    .sort((a, b) => a.id - b.id)
                    .map(plan => (
                      <MenuItem
                        key={plan.id}
                        value={plan.id}
                      >
                        {plan.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <TextField
                label='Data de Início'
                type='date'
                name='start_date'
                fullWidth
                value={subscriptionForm.start_date}
                onChange={handleSubscriptionFormChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label='Data de Término'
                type='date'
                name='end_date'
                fullWidth
                value={subscriptionForm.end_date}
                onChange={handleSubscriptionFormChange}
                InputLabelProps={{ shrink: true }}
              />
              <FormControl
                fullWidth
                margin='normal'
              >
                <InputLabel id='payment-granularity-label'>
                  Granularidade de Pagamento
                </InputLabel>
                <Select
                  labelId='payment-granularity-label'
                  name='payment_granularity'
                  value={subscriptionForm.payment_granularity}
                  onChange={handleSubscriptionFormChange}
                  label='Granularidade de Pagamento'
                >
                  <MenuItem value='daily'>Diária</MenuItem>
                  <MenuItem value='weekly'>Semanal</MenuItem>
                  <MenuItem value='monthly'>Mensal</MenuItem>
                  <MenuItem value='yearly'>Anual</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label='Método de Pagamento'
                name='payment_method'
                fullWidth
                value={subscriptionForm.payment_method}
                onChange={handleSubscriptionFormChange}
              />
              <FormControl
                fullWidth
                margin='normal'
              >
                <InputLabel id='subscription-status-label'>
                  Status da Assinatura
                </InputLabel>
                <Select
                  labelId='subscription-status-label'
                  name='status'
                  value={subscriptionForm.status}
                  onChange={handleSubscriptionFormChange}
                  label='Status da Assinatura'
                >
                  <MenuItem value='pending'>Pendente</MenuItem>
                  <MenuItem value='active'>Ativa</MenuItem>
                  <MenuItem value='canceled'>Cancelada</MenuItem>
                  <MenuItem value='trialing'>Em teste</MenuItem>
                  <MenuItem value='locked'>Bloqueada</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label='Limite de usuários'
                type='number'
                name='user_count'
                fullWidth
                value={subscriptionForm.user_count}
                onChange={handleSubscriptionFormChange}
              />
              <TextField
                label='Valor base por usuário'
                type='number'
                name='base_user_value'
                fullWidth
                value={subscriptionForm.base_user_value}
                onChange={handleSubscriptionFormChange}
              />
              <TextField
                label='Valor de Acréscimo'
                name='add_on_value'
                type='number'
                fullWidth
                value={subscriptionForm.add_on_value}
                onChange={handleSubscriptionFormChange}
              />
              <TextField
                label='Fim do Período de Testes'
                type='date'
                name='trial_ends_at'
                fullWidth
                value={subscriptionForm.trial_ends_at}
                onChange={handleSubscriptionFormChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label='Código de Desconto'
                name='discount_code'
                fullWidth
                value={subscriptionForm.discount_code}
                onChange={handleSubscriptionFormChange}
              />
              <TextField
                label='Valor de Desconto'
                name='discount_value'
                type='number'
                fullWidth
                value={subscriptionForm.discount_value}
                onChange={handleSubscriptionFormChange}
              />

              <Button
                variant='contained'
                color='primary'
                type='submit'
              >
                Atualizar
              </Button>
            </FormContainer>
          </Collapse>
        </div>
      </div>

      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h2>Criar Conta e Assinatura</h2>
          <IconButton
            onClick={() =>
              setShowCreateAccountSection(!showCreateAccountSection)
            }
          >
            {showCreateAccountSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </div>
        <Collapse in={showCreateAccountSection}>
          <FormContainer
            as='form'
            onSubmit={event =>
              saveAccountAndSubscription(
                event,
                newAccountDataForm,
                newSubscriptionForm,
                '',
              )
            }
          >
            {renderAccountSubscriptionFields(
              newAccountDataForm,
              newSubscriptionForm,
              handleNewAccountDataFormChange,
              handleNewSubscriptionFormChange,
            )}
            <Button
              variant='contained'
              color='primary'
              type='submit'
            >
              Criar
            </Button>
          </FormContainer>
        </Collapse>
      </div>

      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h2>Configuração Padrão e de Todas as Contas</h2>
          <IconButton onClick={() => setShowSharedConfig(!showSharedConfig)}>
            {showSharedConfig ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </div>
        <Collapse in={showSharedConfig}>
          <FormContainer as='form'>
            <ConfigurationFields
              data={sharedFormData}
              onChange={handleSharedInputChange}
            />
            <Button
              variant='contained'
              color='primary'
              onClick={event => handleSubmit(event, '', sharedFormData)}
            >
              Atualizar Configuração Padrão (Apenas para novas contas)
            </Button>
            <Button
              variant='contained'
              color='secondary'
              onClick={event =>
                handleSubmit(event, 'all_accounts', sharedFormData)
              }
            >
              Atualizar Configuração de todas as contas
            </Button>
          </FormContainer>
        </Collapse>
      </div>
    </Container>
  )
}

export default AdminCockpit
